import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  ShieldCheck,
  MapPin,
  ChevronRight,
  Shield,
  HeartHandshake,
  Target,
  Phone,
  Navigation,
  Heart,
  Sparkles,
  ArrowRight,
  Clock,
  Calendar,
  HandFist
} from 'lucide-react';
import { CardSkeleton } from '../../components/common/Skeleton';
import { PageHeader } from '../../components/common/PageHeader';
import { getGelanggangDB, getMembersDB, getDonationCampaignsDB, confirmDonationSuccessDB, markDonationFailedDB, getNoticesDB } from '../../services/db';


import { verifyToyyibPayBillStatus } from '../../lib/toyyibpay';
import { GelanggangDetail, UserProfile, DonationCampaign, DonationRecord, NoticeItem } from '../../types';
import { formatPublicName, getGoogleMapsUrl, getGelanggangDisplayAddress, isSuperAdmin, hasRolePermission } from '../../utils/formatters';
import { DonationCard } from '../../components/donations/DonationCard';
import { useToast } from '../../components/common/Toast';
import { getNextScheduledActivity } from '../../utils/activityScheduler';
import { PwaInstallCtaSection } from '../../components/home/PwaInstallCtaSection';

interface LandingPageProps {
  onGoToLogin: () => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGoToLogin }) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [gelanggangs, setGelanggangs] = useState<GelanggangDetail[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());

  const isManagement = Boolean(
    currentUser &&
    hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari'])
  );

  useEffect(() => {
    // Tick every 30 seconds to automatically roll over activities when their end time passes
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 30000);
    return () => clearInterval(timer);
  }, []);

  const loadData = () => {
    // Read from persistent cache first for instant sub-second render
    const cachedG = localStorage.getItem('asmm_db_gelanggang_v5');
    const cachedM = localStorage.getItem('asmm_db_members_v4');
    const cachedC = localStorage.getItem('asmm_donation_campaigns_v1');
    const cachedN = localStorage.getItem('asmm_db_notices_v4');

    if (cachedG && cachedM) {
      setGelanggangs(JSON.parse(cachedG));
      setMembers(JSON.parse(cachedM));
      if (cachedC) {
        try {
          setCampaigns(JSON.parse(cachedC));
        } catch { }
      }
      if (cachedN) {
        try {
          setNotices(JSON.parse(cachedN));
        } catch { }
      }
      setIsLoading(false);
    }

    // Silent background revalidation with Turso DB
    Promise.all([getGelanggangDB(), getMembersDB(), getDonationCampaignsDB(), getNoticesDB()]).then(([gData, mData, cData, nData]) => {
      setGelanggangs(gData);
      setMembers(mData);
      setCampaigns(cData);
      setNotices(nData);
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    loadData();
    const handleDbUpdate = () => loadData();
    window.addEventListener('asmm_db_updated', handleDbUpdate);

    // Auto-verify returning ToyyibPay FPX payment
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search);
      const statusId = searchParams.get('status_id') || searchParams.get('status');
      const billCode = searchParams.get('billcode') || searchParams.get('billCode') || searchParams.get('bill_code');

      if (billCode) {
        verifyToyyibPayBillStatus(billCode).then((res) => {
          if (res.status === 'Disahkan' || statusId === '1') {
            confirmDonationSuccessDB(billCode).then((confirmed) => {
              if (confirmed.donation) {
                showToast('success', 'Alhamdulillah! Bayaran Infaq FPX anda telah disahkan berjaya.');
                window.history.replaceState({}, document.title, '/donations');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }
            });
          } else if (res.status === 'Gagal' || statusId === '3') {
            markDonationFailedDB(billCode).then(() => {
              showToast('error', 'Pembayaran sumbangan FPX tidak berjaya atau telah dibatalkan.');
              window.history.replaceState({}, document.title, window.location.pathname);
            });
          } else if (res.status === 'Menunggu Pengesahan' || statusId === '2') {
            showToast('info', 'Transaksi FPX sedang diproses oleh pihak bank. Sila tunggu pengesahan.');
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        });
      }
    }


    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, []);


  const getLeaderPhone = (g: GelanggangDetail): string => {
    const matched = members.find(
      (m) =>
        m.name.trim().toLowerCase() === g.leaderName.trim().toLowerCase() ||
        (m.role === 'Ketua Gelanggang' && m.gelanggang === g.name)
    );
    return matched?.phone || g.leaderPhone || '';
  };

  const formatWhatsAppUrl = (phone: string, gelanggangName: string): string => {
    let clean = phone.replace(/[^0-9]/g, '');
    if (!clean) return '#';
    if (clean.startsWith('0')) {
      clean = '6' + clean;
    } else if (!clean.startsWith('60') && clean.startsWith('1')) {
      clean = '60' + clean;
    }
    const waText = encodeURIComponent(
      `Salam sejahtera, saya berminat untuk mendaftar anak / diri bagi latihan silat di gelanggang *${gelanggangName}*.`
    );
    return `https://wa.me/${clean}?text=${waText}`;
  };

  const handleOpenDonate = (camp?: DonationCampaign) => {
    if (camp?.id) {
      window.history.pushState(null, '', `/donations/${camp.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else {
      handleNavigateToDonations();
    }
  };

  const handleShare = (camp: DonationCampaign) => {
    const url = typeof window !== 'undefined' ? `${window.location.origin}/donations/${camp.id}` : `https://asmm.pages.dev/donations/${camp.id}`;
    const text = `Sumbang ke tabung *${camp.title}* anjuran Persatuan Seni Silat ASMM Kedah:\n${url}`;
    navigator.clipboard.writeText(text);
    showToast('success', 'Pautan kempen disalin ke papan keratan');
  };

  const handleOpenEdit = (camp?: DonationCampaign) => {
    if (camp?.id) {
      window.history.pushState(null, '', `/donations/${camp.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    } else {
      handleNavigateToDonations();
    }
  };

  const handleNavigateToDonations = () => {
    window.history.pushState(null, '', '/donations');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };


  const featuredCampaign = campaigns.find((c) => c.isFeatured && c.status === 'Aktif') || campaigns[0];
  const secondaryCampaigns = campaigns.filter((c) => c.id !== featuredCampaign?.id && c.status === 'Aktif').slice(0, 2);

  return (
    <div className="w-full space-y-12 min-h-[75vh] bg-white text-slate-900 pb-16">
      {/* Inspiring Emotion-Driven Hero Section (UI/UX Pro Max + PSSASMM Identity) */}
      <section className="text-center pt-8 sm:pt-12 pb-6">
        {/* Eyebrow Pill Tag */}
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-700 text-xs font-bold mb-5 shadow-xs">
          <ShieldCheck className="w-4 h-4 text-red-600 shrink-0" />
          <span>Platform Rasmi Pertubuhan Seni Silat Awang Sulung Merah Muda</span>
        </div>

        {/* Display-LG Main Headline */}
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight max-w-4xl mx-auto leading-tight sm:leading-tight">
          Tempat Lahirnya Pendekar Berhemah & Warisan Sejati
        </h1>

        {/* Subtitle */}
        <p className="text-sm sm:text-base text-slate-600 font-medium mt-4 max-w-2xl mx-auto leading-relaxed">
          Menyatukan disiplin seni bela diri warisan Melayu berakar dari Pattani dengan sistem pengurusan berstruktur, telus, dan berintegriti.
        </p>

        {/* Primary Action Pill CTA */}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3.5">
          <button
            onClick={() => {
              const target = currentUser ? '/dashboard' : '/login';
              window.history.pushState(null, '', target);
              window.dispatchEvent(new PopStateEvent('popstate'));
            }}
            className="px-8 py-3.5 bg-red-600 hover:bg-red-700 text-white font-bold text-xs sm:text-sm rounded-full shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5 flex items-center gap-2 cursor-pointer group"
          >
            <span>{currentUser ? 'Dashboard Ahli' : 'Daftar Sekarang'}</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>

        {/* Hero Visual Brand Emblem & Philosophy Card with Overlapping Badge */}
        <div className="mt-12 max-w-2xl mx-auto relative pt-8">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white shadow-md bg-white p-2.5 flex items-center justify-center">
              <img
                src="/logo-pssasmm.png"
                alt="Logo PSSASMM"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-100 p-6 sm:p-8 pt-14 sm:pt-16 shadow-sm text-center">
            <span className="text-[11px] font-bold text-amber-600 uppercase tracking-widest block">
              Falsafah Pendekar Awang Sulung
            </span>
            <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 leading-snug mt-1">
              "Biduk Berlalu, Kiambang Bertaut"
            </h3>
            <p className="text-xs sm:text-sm text-slate-600 italic leading-relaxed mt-2 max-w-lg mx-auto">
              "Nilai sebenar seorang pendekar terletak pada kelembutan hatinya memaafkan. Silat itu mencari kawan, bukan menempah lawan."
            </p>
          </div>
        </div>

        {/* Floating Metric Pill Bar */}
        <div className="bg-white rounded-3xl sm:rounded-full py-3.5 px-6 sm:px-10 shadow-sm border border-slate-200/80 flex flex-wrap items-center justify-around gap-4 sm:gap-8 max-w-3xl mx-auto mt-8">
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-black text-slate-900">{gelanggangs.filter(g => g.status === 'Aktif').length || 5}</div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Gelanggang Rasmi</div>
          </div>
          <div className="hidden sm:block h-7 w-px bg-slate-200" />
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-black text-amber-600">{members.length || '500+'}</div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Pesilat Aktif</div>
          </div>
          <div className="hidden sm:block h-7 w-px bg-slate-200" />
          <div className="text-center">
            <div className="text-lg sm:text-2xl font-black text-red-600">100%</div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Patuh e-ROS</div>
          </div>
        </div>
      </section>

      {/* 3. Interactive 3-Step "How It Works" Section ("Menyertai ASMM Adalah Mudah, Berstruktur & Dipercayai") */}
      <section className="bg-slate-50/70 rounded-3xl border border-slate-100 p-6 sm:p-10">
        <div className="text-center mb-8">
          <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
            Mulakan Perjalanan Silat Anda
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Tiga langkah mudah untuk anda menyertai persatuan seni silat ASMM
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Step 1 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-xs flex flex-col items-start text-left">
            <div className="w-10 h-10 rounded-full bg-red-600 text-white font-extrabold flex items-center justify-center text-base mb-4 shadow-xs">
              1
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1.5">Pilih Gelanggang & Cipta Profil</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Semak jadual latihan gelanggang berhampiran dan lengkapkan pendaftaran ahli secara digital dalam 2 minit.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-xs flex flex-col items-start text-left">
            <div className="w-10 h-10 rounded-full bg-slate-900 text-white font-extrabold flex items-center justify-center text-base mb-4 shadow-xs">
              2
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1.5">Hadir Sesi Latihan Rasmi</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Pelajari kurikulum bengkung secara berperingkat dibimbing Jurulatih bertauliah.
            </p>
          </div>

          {/* Step 3 */}
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-xs flex flex-col items-start text-left">
            <div className="w-10 h-10 rounded-full bg-amber-500 text-white font-extrabold flex items-center justify-center text-base mb-4 shadow-xs">
              3
            </div>
            <h3 className="font-bold text-slate-900 text-base mb-1.5">Raih Cula & Sumbang Jasa</h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              Nilai kemahiran melalui ujian bengkung rasmi, raih cula kependekaran, dan sertai pelbagai aktiviti.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Curated Gelanggang Discovery Grid */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-extrabold text-lg text-slate-900">Pusat Latihan (Gelanggang Awam)</h3>
            <p className="text-xs text-slate-500">Pilih gelanggang pilihan anda untuk maklumat sesi latihan dan lokasi Google Maps.</p>
          </div>
        </div>

        {/* Gelanggang Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {isLoading ? (
            <>
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </>
          ) : (
            gelanggangs.filter(g => g.status === 'Aktif').map((g) => {
              const leaderPhone = getLeaderPhone(g);
              const leaderPublicName = formatPublicName(g.leaderName);
              const mapsUrl = getGoogleMapsUrl(g);
              const nextActivity = getNextScheduledActivity(g, notices, currentTime);

              return (
                <div key={g.id} className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-sm flex flex-col justify-between h-full hover:shadow-md transition-all">
                  <div className="flex flex-col flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-base text-slate-900 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-red-600 shrink-0" />
                        {g.name}
                      </h3>
                      <span className="text-[10px] bg-red-50 text-red-700 font-bold px-2.5 py-0.5 rounded-full border border-red-100">
                        {g.status}
                      </span>
                    </div>

                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-xs text-slate-600 hover:text-red-600 font-medium leading-relaxed group/addr min-h-[2.5rem] mb-3 flex items-start"
                      title="Klik untuk membuka lokasi di Google Maps"
                    >
                      <span className="group-hover/addr:underline line-clamp-2">{getGelanggangDisplayAddress(g)}</span>
                    </a>

                    <div className="mt-auto text-xs text-slate-700 bg-slate-50 p-3.5 rounded-2xl border border-slate-100 space-y-1.5 font-medium">
                      <div>Sesi: <strong className="text-slate-900">{g.sessionTime}</strong></div>
                      <div>Ketua: <strong className="text-red-600">{leaderPublicName}</strong></div>
                      {leaderPhone && (
                        <div className="pt-0.5">
                          <a
                            href={formatWhatsAppUrl(leaderPhone, g.name)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono text-slate-800 hover:text-red-600 font-bold hover:underline cursor-pointer group/wa"
                            title={`Hubungi ${leaderPublicName} menerusi WhatsApp`}
                          >
                            <Phone className="w-3.5 h-3.5 text-red-600 group-hover/wa:scale-110 transition-transform shrink-0" />
                            <span>{leaderPhone}</span>
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Next Scheduled Activity Banner */}
                    {nextActivity ? (
                      <div
                        className={`mt-3 p-3 rounded-2xl border transition-all ${nextActivity.isLive
                          ? 'bg-red-50/50 border-red-200 text-red-950 shadow-xs'
                          : nextActivity.isToday
                            ? 'bg-amber-50/50 border-amber-200 text-amber-950 shadow-xs'
                            : 'bg-slate-50 border-slate-100 text-slate-800'
                          }`}
                      >
                        <div className="flex items-center justify-between gap-1.5 mb-1.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className={`p-1 rounded-lg shrink-0 ${nextActivity.isLive
                                ? 'bg-red-100 text-red-700'
                                : nextActivity.isToday
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-200/80 text-slate-700'
                                }`}
                            >
                              {nextActivity.type === 'latihan' ? (
                                <HandFist className="w-3.5 h-3.5" />
                              ) : (
                                <Calendar className="w-3.5 h-3.5" />
                              )}
                            </span>
                            <span className="text-[11px] font-bold truncate">
                              {nextActivity.isLive
                                ? 'Aktiviti Sedang Berlangsung'
                                : nextActivity.isToday
                                  ? 'Aktiviti Hari Ini'
                                  : 'Aktiviti Seterusnya'}
                            </span>
                          </div>

                          {nextActivity.isLive ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-red-600 text-white shadow-2xs animate-pulse shrink-0">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                              LIVE
                            </span>
                          ) : nextActivity.isToday ? (
                            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-amber-200 text-amber-900 border border-amber-300 shrink-0">
                              Hari Ini
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-200/80 text-slate-700 shrink-0">
                              {nextActivity.dayRelative}
                            </span>
                          )}
                        </div>

                        <div className="space-y-1">
                          <div className="text-xs font-bold text-slate-900 line-clamp-1">
                            {nextActivity.title}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-600 font-medium">
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
                              <strong className="text-slate-800">{nextActivity.dateLabel}</strong>
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3 text-slate-400 shrink-0" />
                              <span className="font-semibold text-slate-700">{nextActivity.timeRange}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 p-2.5 rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-[11px] text-slate-500 text-center">
                        Tiada aktiviti dijadualkan dalam masa terdekat
                      </div>
                    )}
                  </div>

                  {/* Navigation Link Button */}
                  <div className="pt-3">
                    <a
                      href={mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full inline-flex items-center justify-center gap-2 text-xs font-extrabold text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200/80 py-2.5 rounded-full transition-all group/nav cursor-pointer shadow-2xs"
                    >
                      <Navigation className="w-4 h-4 text-blue-600 group-hover/nav:scale-110 transition-transform shrink-0" />
                      <span>Petunjuk Arah Google Maps</span>
                    </a>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {/* 5. Key Solution Pillars Grid */}
      <section className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-sm space-y-4">
        <h3 className="text-base font-bold text-slate-900 text-center">
          Tiga Teras Pembentukan Sahsiah & Pertahanan Diri ASMM
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
            <div className="flex items-center gap-2 font-bold text-slate-900">
              <Shield className="w-4 h-4 text-red-600 shrink-0" />
              <span>1. Perlindungan Diri Realistik</span>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Refleks pertahanan diri pantas yang dibina menerusi latihan jurus dan buah pukulan berkesan.
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
            <div className="flex items-center gap-2 font-bold text-slate-900">
              <Target className="w-4 h-4 text-amber-600 shrink-0" />
              <span>2. Disiplin & Ketahanan Mental</span>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Membina daya fokus tinggi, kawalan emosi, dan jati diri berdisiplin untuk mendepani sebarang ancaman.
            </p>
          </div>

          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
            <div className="flex items-center gap-2 font-bold text-slate-900">
              <HeartHandshake className="w-4 h-4 text-blue-600 shrink-0" />
              <span>3. Adab & Ukhuwah Perguruan</span>
            </div>
            <p className="text-slate-600 leading-relaxed">
              Menghormati ibu bapa, guru, dan rakan seiman berpaksikan nilai-nilai murni warisan Melayu.
            </p>
          </div>
        </div>
      </section>

      {/* 6. Infaq / Crowdfunding Section on Homepage */}
      {featuredCampaign && (
        <section className="space-y-3 pt-2">
          <div className="flex items-center justify-between">
            <h3 className="font-extrabold text-base text-slate-900 flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-600 fill-red-600" />
              <span>Tabung Infaq & Pembangunan Ummah</span>
            </h3>

            <button
              type="button"
              onClick={handleNavigateToDonations}
              className="inline-flex items-center gap-1 text-xs font-bold text-slate-600 hover:text-red-600 transition-colors cursor-pointer group"
            >
              <span>Semua ({campaigns.length})</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {/* Featured Infaq Card */}
          <DonationCard
            campaign={featuredCampaign}
            featured={true}
            onDonate={handleOpenDonate}
            onShare={handleShare}
            onEdit={handleOpenEdit}
            isManagement={isManagement}
          />
        </section>
      )}

      {/* 7. PWA Installation & Homescreen Shortcut CTA Section */}
      <PwaInstallCtaSection />

    </div>
  );
};
